import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Droplets, Package, Flame } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Hero: React.FC = () => {
  const { t } = useLanguage();

  const features = [
    { icon: Droplets, label: t('hero.features.pureOils') },
    { icon: Package, label: t('hero.features.apartmentReady') },
    { icon: Flame, label: t('hero.features.miltonMade') },
  ];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-parchment via-[#F3EDE0] to-mist">

      {/* Subtle botanical background pattern */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large soft circles */}
        <motion.div
          className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-herb-light/10"
          animate={{ scale: [1, 1.05, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-amber-herb/8"
          animate={{ scale: [1, 1.08, 1], opacity: [0.2, 0.35, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        <motion.div
          className="absolute top-1/3 left-1/4 w-48 h-48 rounded-full bg-herb-green/5"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />

        {/* Floating herb leaves */}
        {[
          { x: '8%', y: '20%', delay: 0, rotate: -15 },
          { x: '88%', y: '15%', delay: 1.5, rotate: 20 },
          { x: '15%', y: '70%', delay: 3, rotate: -25 },
          { x: '82%', y: '65%', delay: 0.8, rotate: 10 },
          { x: '50%', y: '85%', delay: 2.2, rotate: -5 },
        ].map((leaf, i) => (
          <motion.div
            key={i}
            className="absolute opacity-20"
            style={{ left: leaf.x, top: leaf.y }}
            animate={{ y: [0, -14, 0], rotate: [leaf.rotate, leaf.rotate + 5, leaf.rotate] }}
            transition={{ duration: 7 + i, repeat: Infinity, delay: leaf.delay, ease: 'easeInOut' }}
          >
            <svg width="24" height="38" viewBox="0 0 24 38" fill="none">
              <path d="M12 2C19 6 22 18 12 36C2 18 5 6 12 2Z" fill="#4A7C59" />
              <line x1="12" y1="2" x2="12" y2="36" stroke="#4A7C59" strokeWidth="0.8" opacity="0.5" />
            </svg>
          </motion.div>
        ))}
      </div>

      <div className="container mx-auto px-6 py-28 text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="max-w-4xl mx-auto"
        >
          {/* Eyebrow tag */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="inline-flex items-center px-4 py-1.5 rounded-full bg-herb-green/10 border border-herb-green/20 mb-8"
          >
            <span className="text-herb-green text-sm font-medium tracking-wide">
              Launching Spring 2026 · Boston, MA
            </span>
          </motion.div>

          {/* Main headline */}
          <motion.h1
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            <span className="block text-5xl md:text-7xl font-elegant font-bold text-forest leading-tight">
              {t('hero.title.line1')}
            </span>
            <span className="block text-5xl md:text-7xl font-elegant font-bold text-herb-green leading-tight">
              {t('hero.title.line2')}
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-lg md:text-xl text-warm-gray leading-relaxed max-w-2xl mx-auto mb-10"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {t('hero.subtitle')}
          </motion.p>

          {/* Feature pills */}
          <motion.div
            className="flex flex-wrap justify-center gap-4 mb-12"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {features.map((f, i) => (
              <div
                key={i}
                className="flex items-center gap-2 px-4 py-2 bg-white/70 rounded-full border border-herb-light/30 shadow-sm"
              >
                <f.icon className="w-4 h-4 text-herb-green" />
                <span className="text-sm font-medium text-charcoal">{f.label}</span>
              </div>
            ))}
          </motion.div>

          {/* CTA buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <motion.button
              whileHover={{ scale: 1.03, boxShadow: '0 8px 30px rgba(74, 124, 89, 0.25)' }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-herb-green text-white font-semibold rounded-full shadow-md transition-all duration-300"
            >
              {t('hero.cta')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white/80 text-forest font-semibold rounded-full border border-herb-green/25 hover:border-herb-green/50 transition-all duration-300"
            >
              {t('hero.ctaSecondary')}
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowDown className="w-5 h-5 text-warm-gray/50" />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
